import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";

import { useAppSelector } from "app/hooks";
import { SALES_COACH_DASHBOARD_FEATURE } from "constants/feature-flags";
import { selectLDIdentified } from "features/auth/auth-slice";

/**
 * How long to keep waiting for LaunchDarkly before deciding it is never coming.
 *
 * Bounded on purpose: without it, a blocked or offline LaunchDarkly leaves the section
 * spinning forever, which is worse than the redirect this hook exists to prevent. The real
 * case — flags arriving a beat after `identify()` resolves — settles in well under a second,
 * so this only ever runs out when something is genuinely wrong.
 */
const FLAG_DELIVERY_GRACE_MS = 3_000;

export interface SalesCoachRouteAccess {
  /** Render a fallback. The answer is not knowable yet. */
  isResolving: boolean;
  /** Only meaningful once `isResolving` is false. */
  isEnabled: boolean;
}

/**
 * Whether this browser may enter `/sales-coach`, and whether that is even decided yet.
 *
 * **The bug this exists for: `ldIdentified` is not evidence that the flag has a value.**
 * `use-authorize.hook.tsx` dispatches `setLDIdentified(true)` after
 * `await ldClient?.identify(...)` — and that call is OPTIONAL-CHAINED, so if the LD client
 * is not constructed yet the await resolves to `undefined` immediately and we mark
 * ourselves identified holding an empty flag set. The route then read
 * `flags[SALES_COACH_DASHBOARD_FEATURE] ?? false`, found `false`, and answered a deep link
 * with `<Navigate to="/" replace />`.
 *
 * `replace` is what makes it more than a flicker: the URL the user actually asked for is
 * gone from history, so `/sales-coach/playbook?play=C.say_i_dont_know` becomes the home
 * screen with no way back. It only reproduces on a cold load of a direct link, because
 * in-app navigation happens long after the flags have landed.
 *
 * **`Object.keys(flags).length` is the signal, not the flag's own value.** LaunchDarkly
 * delivers a context's entire flag payload in one go, so an empty object means "nothing has
 * arrived" while a populated one without our key means the flag genuinely does not exist for
 * this context — which is correctly `false`. Reading only our key cannot tell those apart,
 * and that is precisely the confusion that shipped.
 *
 * This is the same bug class as ENG-1668 (Action Items) and ENG-664 (Collections), both
 * noted in `routes.tsx`. Sales Coach deliberately did not copy the fix used there — a
 * `location.pathname.startsWith(...)` clause that keeps the route registered — because that
 * lets someone reach the page with the flag off, and at the time the page rendered
 * illustrative mock data. **That reason is now stale**: the section runs on the real
 * `/coach/` API and the mock prototype was deleted, so nothing fabricated can leak. It is
 * still not the fix to adopt, though, because it would make the flag bypassable by typing a
 * URL. Waiting for a real answer keeps the gate and fixes the link.
 */
export default function useSalesCoachRouteAccess(): SalesCoachRouteAccess {
  const identified = useAppSelector(selectLDIdentified);
  const flags = useFlags();
  const hasFlagPayload = Object.keys(flags).length > 0;

  const [graceExpired, setGraceExpired] = useState(false);

  useEffect(() => {
    if (hasFlagPayload) return undefined;
    const timer = setTimeout(
      () => setGraceExpired(true),
      FLAG_DELIVERY_GRACE_MS
    );
    return () => clearTimeout(timer);
  }, [hasFlagPayload]);

  return {
    isResolving: !graceExpired && (!identified || !hasFlagPayload),
    // Never a truthy coercion: an absent flag and a flag set to false must both read as off.
    isEnabled: flags[SALES_COACH_DASHBOARD_FEATURE] === true
  };
}
